import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { AdvisorAccount } from "@/utils/accounting";
import { formatCOP } from "@/utils/format";

interface AdvisorPaymentModalProps {
  open: boolean;
  account: AdvisorAccount | null;
  onClose: () => void;
  onSubmit: (amount: number, note: string) => void;
}

const AdvisorPaymentModal = ({ open, account, onClose, onSubmit }: AdvisorPaymentModalProps) => {
  const [amount, setAmount] = useState(0);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setAmount(0);
    setNote("");
    setError("");
  }, [open]);

  const submit = () => {
    const v = Number(amount) || 0;
    if (v <= 0) {
      setError("El abono debe ser mayor a cero.");
      return;
    }
    if (account && v > account.debt) {
      setError(`El abono no puede superar la deuda de ${formatCOP(account.debt)}.`);
      return;
    }
    onSubmit(v, note.trim());
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Cobro · ${account ? `${account.advisor.names} ${account.advisor.surnames}` : ""}`}
      icon="ri-hand-coin-line"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-check-double-line" /> Registrar abono
          </button>
        </>
      }
    >
      <div className="rounded-md bg-background-200/40 border border-background-300/40 px-4 py-3 mb-4">
        <div className="flex items-center justify-between text-xs text-foreground-600">
          <span>Deuda actual</span>
          <span className="font-bold text-foreground-950">{formatCOP(account?.debt ?? 0)}</span>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className={labelClass}>Monto del abono (COP)</label>
          <input
            type="number"
            min={0}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Nota (opcional)</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ej: abono en efectivo"
            className={inputClass}
          />
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default AdvisorPaymentModal;